import { useCallback, useState } from 'react';

import { getCookie, setCookie } from '../helpers/cookies';

const PINNED_ORGS_COOKIE_NAME = 'pinnedOrganizations';
const COOKIE_EXPIRY_DAYS = 365; // 1 year

/**
 * Hook to manage pinned organizations stored in cookies.
 * Pinned orgs are stored as a JSON array of organization IDs.
 */
export const usePinnedOrganizations = () => {
  const [pinnedOrgIds, setPinnedOrgIds] = useState<Set<number>>(() => {
    const cookieValue = getCookie(PINNED_ORGS_COOKIE_NAME);
    if (!cookieValue) {
      return new Set<number>();
    }
    try {
      const parsed = JSON.parse(cookieValue) as number[];
      return new Set(parsed);
    } catch {
      return new Set<number>();
    }
  });

  const savePinnedToCookie = useCallback((ids: Set<number>) => {
    const idsArray = Array.from(ids);
    setCookie(PINNED_ORGS_COOKIE_NAME, JSON.stringify(idsArray), COOKIE_EXPIRY_DAYS);
  }, []);

  const pinOrganization = useCallback(
    (orgId: number) => {
      setPinnedOrgIds((prev) => {
        const next = new Set(prev);
        next.add(orgId);
        savePinnedToCookie(next);
        return next;
      });
    },
    [savePinnedToCookie],
  );

  const unpinOrganization = useCallback(
    (orgId: number) => {
      setPinnedOrgIds((prev) => {
        const next = new Set(prev);
        next.delete(orgId);
        savePinnedToCookie(next);
        return next;
      });
    },
    [savePinnedToCookie],
  );

  const isPinned = useCallback((orgId: number) => pinnedOrgIds.has(orgId), [pinnedOrgIds]);

  const togglePin = useCallback(
    (orgId: number) => {
      if (isPinned(orgId)) {
        unpinOrganization(orgId);
      } else {
        pinOrganization(orgId);
      }
    },
    [isPinned, unpinOrganization, pinOrganization],
  );

  return {
    pinnedOrgIds,
    pinOrganization,
    unpinOrganization,
    isPinned,
    togglePin,
  };
};
